import React, { createContext, useContext, useMemo } from 'react'

import {
  BlockDimensions,
  DynamicSectionBlock,
  DynamicSectionConfig,
  DynamicSectionLayout,
  getBlockDimensions
} from '@toasttab/sites-components'

import { Block as BlockType } from 'src/apollo/sites'

import { getBlockRowsAndCols } from './DynamicSectionUtils'

export type SectionLayout = DynamicSectionLayout<BlockType>

const DynamicSectionConfigContext = createContext<DynamicSectionConfig | null>(
  null
)
const DynamicSectionLayoutContext = createContext<SectionLayout | null>(null)

export const DynamicSectionConfigProvider = ({
  config,
  children
}: React.PropsWithChildren<{ config: DynamicSectionConfig }>) => (
  <DynamicSectionConfigContext.Provider value={config}>
    {children}
  </DynamicSectionConfigContext.Provider>
)

export const DynamicSectionLayoutProvider = ({
  layout,
  children
}: React.PropsWithChildren<{ layout: SectionLayout }>) => (
  <DynamicSectionLayoutContext.Provider value={layout}>
    {children}
  </DynamicSectionLayoutContext.Provider>
)

export const useDynamicSectionConfig = (): DynamicSectionConfig => {
  const config = useContext(DynamicSectionConfigContext)
  if (!config) {
    throw new Error(
      'useDynamicSectionConfig must be used within a DynamicSectionConfigProvider'
    )
  }
  return config
}

export const useDynamicSectionLayout = (): SectionLayout => {
  const layout = useContext(DynamicSectionLayoutContext)
  if (!layout) {
    throw new Error(
      'useDynamicSectionLayout must be used within a DynamicSectionLayoutProvider'
    )
  }
  return layout
}

export const useBlockDimensionUtils = () => {
  const {
    horizontalMargins: margins,
    screenType,
    mobileNumCols,
    maxContentWidth
  } = useDynamicSectionConfig()

  return useMemo(
    () => ({
      rowsAndColsFor: (block: DynamicSectionBlock) =>
        getBlockRowsAndCols(block, margins, maxContentWidth),
      dimensionsFor: (
        block: DynamicSectionBlock,
        mobileOffset = 0
      ): BlockDimensions =>
        getBlockDimensions(
          block,
          screenType,
          mobileOffset,
          margins,
          mobileNumCols,
          maxContentWidth
        ),
      mobileDimensionsFor: (
        block: DynamicSectionBlock,
        mobileOffset = 0
      ): BlockDimensions =>
        getBlockDimensions(
          block,
          'mobile',
          mobileOffset,
          margins,
          mobileNumCols,
          maxContentWidth
        )
    }),
    [margins, screenType, mobileNumCols, maxContentWidth]
  )
}
